import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEmailModalProps } from "./change-email";
import { useGlobalNotifications } from "../../../../../../hooks/use-global-notifications";
import { api } from "../../../../../../api/api";
import { useAuth } from "../../../../../../contexts/auth/auth-context";

type UseChangeEmailModalParams = ChangeEmailModalProps;

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export function useChangeEmailModal({
    open,
    initialEmail,
    onClose,
    onSave,
}: UseChangeEmailModalParams) {
    const { showSuccess, showError, showLoading, hideNotification } =
        useGlobalNotifications();
    const { email: authEmail, profile } = useAuth();
    // Prefer profile email (updated by syncSession) over auth form email
    const userEmail = profile?.email_address || authEmail || "";

    const [email, setEmail] = useState(initialEmail ?? "");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const panelRef = useRef<HTMLDivElement | null>(null);

    // Reset when opened with fresh initial
    useEffect(() => {
        if (!open) return;
        setEmail(initialEmail ?? "");
        setPassword("");
        setShowPwd(false);
        setError(null);
    }, [open, initialEmail]);

    // Esc to close
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const disabled = useMemo(() => {
        return !isValidEmail(email) || !password.trim() || saving;
    }, [email, password, saving]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!isValidEmail(email)) {
            setError("Please enter a valid email address.");
            return;
        }
        if (!password.trim()) {
            setError("Please enter your current password.");
            return;
        }

        try {
            setSaving(true);
            showLoading("Verifying your password...");

            await api.post("/auth/signin-password", {
                email: userEmail,
                password: password,
            });

            showLoading("Updating your email...");
            await onSave({ email: email.trim(), currentPassword: password });
            hideNotification();
            showSuccess("Email updated successfully");
        } catch (err: any) {
            hideNotification();
            let errorMsg = "Could not change email. Please try again.";

            if (err?.response?.status === 401 || err?.response?.status === 400) {
                errorMsg = "Current password is incorrect.";
            } else if (err?.response?.data?.detail) {
                errorMsg = err.response.data.detail;
            } else if (err?.message) {
                errorMsg = err.message;
            }

            setError(errorMsg);
            showError(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPwd((s) => !s);
    };

    return {
        panelRef,
        email,
        setEmail,
        password,
        setPassword,
        showPwd,
        handleTogglePasswordVisibility,
        error,
        saving,
        disabled,
        handleSave,
    };
}
